import { Button } from "../../../../../../components/New/Button";
import { ModalSubTitle } from "../../../../../../components/New/Modal/styles";
import { SelectInput } from "../../../../../../components/SelectInput";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import * as S from "./styles";
import { useEffect, useState } from "react";
import { wrapperRequests } from "../../../../../../services/api";
import { routesURL } from "../../../../../../services/routesUrl";
import { StoreToRefund } from "../../../../../../@types/Stores";
import { SearchableSelectInput } from "../../../../../../components/New/SearchableSelectInput";
import { toastify } from "../../../../../../shared/Toastify";
import { useMediaQuery } from "usehooks-ts";
import { useTheme } from "styled-components";
import { ResponsiveButtonContainer } from "../RefundModal/styles";
import { Language } from "../../../../../../enums/Language";
import { useTranslateConstants } from "../../hooks/useTranslateConstants";
import { useAuth } from "../../../../../../hooks/auth";
import { newColors } from "../../../../../../styles/themes/default/New/foundations/colors";
import ReactLoading from "react-loading";
import { OrderDetailSale } from "../../../../../../@types/Orders";

interface CancelModalProps {
  order: {
    orderId: string;
    sourceMarketplace:
      | "Incycle Shopify"
      | "RnR Shopify"
      | "Incycle Ebay"
      | "SBC";
    sales: OrderDetailSale[];
  };
  setCloseDrawer?: () => void;
  onSuccess?: () => void;
}

export function CancelModal({
  setCloseDrawer,
  order,
  onSuccess,
}: CancelModalProps) {
  const { t, i18n } = useTranslation();
  const { internalReasonCancel, shopifyCancelReason, ebayCancelReason } =
    useTranslateConstants();

  const { orderId, sourceMarketplace, sales } = order;

  function getReasonOptions({
    sourceMarketplace,
  }: {
    sourceMarketplace: string;
  }) {
    if (sourceMarketplace.toLowerCase().includes("ebay")) {
      return ebayCancelReason;
    } else return shopifyCancelReason;
  }
  const { canRefundAndCancelByCountry, isUnitedStates } = useAuth();

  const [allStores, setAllStores] = useState<StoreToRefund[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState(
    getReasonOptions({
      sourceMarketplace,
    })[0].value,
  );
  const [internalReason, setInternalReason] = useState("");
  const [selectedStore, setSelectedStore] = useState({
    id: "63647232c09b1fa35103bec6",
    tenantName: "Incycle",
    name: t("pomonaFulfillmentCenter"),
    code: "9",
  });

  const { responsiveDesign } = useTheme();
  const isResponsive = useMediaQuery(`(max-width: ${responsiveDesign.sm})`);

  const isEnglishLanguage = i18n.language === Language.ENGLISH;

  const handleCancelOrder = async () => {
    if (!internalReason) {
      setErrorMessage(t("cancelationInternalReasonRequired"));
      return;
    }

    setLoading(true);
    try {
      await wrapperRequests(
        routesURL.report.orders.cancelOrder(orderId),
        "POST",
        {
          data: {
            reason,
            shopID: selectedStore.code,
            tenantName: selectedStore.tenantName,
            internalReason: internalReason,
          },
        },
      );

      toastify("success", t("cancelationCompleted"), "top-center");

      if (onSuccess) {
        onSuccess();
      }

      if (setCloseDrawer) {
        setCloseDrawer();
      }
    } catch (error) {
      toastify("error", t("cancelationError"), "top-center");
    } finally {
      setLoading(false);
    }
  };

  const findStoreFromSale = (
    stores: StoreToRefund[],
    sale: OrderDetailSale,
  ): StoreToRefund | undefined => {
    return stores.find(
      (store) =>
        store.tenantName?.toLowerCase() === sale.tenantName?.toLowerCase() &&
        store.code === sale.shopID,
    );
  };

  const selectInitialStore = (stores: StoreToRefund[]) => {
    const firstSale = sales?.[0];

    if (isUnitedStates) {
      const pomonaStore = stores.find(
        (store) =>
          store.name?.toLowerCase().includes("pomona") &&
          store.tenantName?.toLowerCase() === "incycle",
      );

      if (pomonaStore) {
        return {
          id: pomonaStore.id,
          tenantName: pomonaStore.tenantName,
          name: pomonaStore.name,
          code: pomonaStore.code,
        };
      }
    }

    if (firstSale) {
      const storeFromSale = findStoreFromSale(stores, firstSale);
      if (storeFromSale) {
        return {
          id: storeFromSale.id,
          tenantName: storeFromSale.tenantName,
          name: storeFromSale.name,
          code: storeFromSale.code,
        };
      }
    }

    return stores[0];
  };

  const getAllStoresOptions = async () => {
    try {
      const response = await wrapperRequests(
        routesURL.settings.stores.getCustomAllStores([
          "name",
          "displayName",
          "code",
          "tenantName",
        ]),
        "GET",
      );

      const stores = response.data.content;
      setAllStores(stores);
      setSelectedStore(selectInitialStore(stores));
    } catch (error) {
      setSelectedStore(allStores[0]);
      throw Error(String(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllStoresOptions();
  }, []);

  return (
    <S.ModalContainer loading={loading}>
      {loading && (
        <ReactLoading
          height={25}
          width={25}
          type={"spin"}
          color={newColors["gray-600"]}
        />
      )}

      {!loading && allStores[0] && (
        <>
          <ModalSubTitle style={{ marginBottom: 16 }}>
            {t("selectReasonForCancelation")}
          </ModalSubTitle>

          <SelectInput
            options={getReasonOptions({ sourceMarketplace })}
            variant="resized"
            onChange={(e) => setReason(e.target.value)}
          />

          <ModalSubTitle style={{ marginBottom: 16, marginTop: 16 }}>
            {t("cancelationInternalReasonCode")}
          </ModalSubTitle>

          <SelectInput
            error={errorMessage}
            options={internalReasonCancel}
            variant="resized"
            onChange={(e) => {
              setInternalReason(e.target.value);
              setErrorMessage("");
            }}
          />

          {canRefundAndCancelByCountry && (
            <>
              <ModalSubTitle style={{ marginTop: 24, marginBottom: 16 }}>
                {t("selectStoreForReturn")}
              </ModalSubTitle>

              <SearchableSelectInput
                initialValue={selectedStore}
                options={allStores}
                onOptionSelect={(store) => {
                  setSelectedStore(store);
                }}
                disabled={loading}
              />
            </>
          )}

          {isResponsive && setCloseDrawer && (
            <ResponsiveButtonContainer>
              <Button
                isLoading={loading}
                onClick={handleCancelOrder}
                title={t("submitCancelation")}
              />
              <Button
                variant="ghost"
                title={t("cancel")}
                isLoading={loading}
                onClick={() => setCloseDrawer()}
              />
            </ResponsiveButtonContainer>
          )}

          {!isResponsive && (
            <S.ButtonAlign>
              <Dialog.Close asChild>
                <Button
                  variant="ghost"
                  title={t("cancel")}
                  width="8.2rem"
                  disabled={loading}
                />
              </Dialog.Close>
              <Button
                title={t("submitCancelation")}
                width={!isEnglishLanguage ? "12.5rem" : "10.5rem"}
                disabled={loading}
                isLoading={loading}
                onClick={handleCancelOrder}
              />
            </S.ButtonAlign>
          )}
        </>
      )}
    </S.ModalContainer>
  );
}
